import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import QuestionReports from './pages/QuestionReports';
import Questions from './pages/Questions';
import QuestionView from './pages/QuestionView';
import QuestionEdit from './pages/QuestionEdit';
import QuestionImportExport from './pages/QuestionImportExport';
import ReviewQueue from './pages/ReviewQueue';
import Subjects from './pages/Subjects';
import ReferralCodes from './pages/ReferralCodes';
import Users from './pages/Users';
import Exams from './pages/Exams';
import Analytics from './pages/Analytics';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#e3f2fd',
    },
    secondary: {
      main: '#dc004e',
    },
    success: {
      main: '#2e7d32',
      light: '#e8f5e9',
    },
    warning: {
      main: '#ed6c02',
      light: '#fff3e0',
    },
    info: {
      main: '#0288d1',
      light: '#e1f5fe',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

// Placeholder components (to be implemented)
const Profile = () => <div>Profile Page - Coming Soon</div>;
const Settings = () => <div>Settings Page - Coming Soon</div>;

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Questions />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/subjects"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Subjects />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionEdit />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionView />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionEdit />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions/review"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ReviewQueue />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/questions/import-export"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionImportExport />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/question-reports"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionReports />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/referral-codes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ReferralCodes />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/exams"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Exams />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Analytics />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Default Route */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* 404 Route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
